import React, { useContext } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Fade } from "react-awesome-reveal";
import { LinkContext } from "../../LinkProvider";

import oceanImg from "../../img/trading/banner-ocean.webp";
import iceEuro from "../../img/trading/ice-euro.webp";
import iceAmazon from "../../img/trading/ice-amazon.webp";
import iceApple from "../../img/trading/ice-apple.webp";
import iceBitcoin from "../../img/trading/ice-bitcoin.webp";
import iceBitcoinmob from "../../img/trading/ice-btc-mob.webp";
import iceTesla from "../../img/trading/ice-tesla.webp";

const BTN_GRADIENT_LIGHT =
  "linear-gradient(-84deg, rgba(14,28,54,0.4) 6%, rgba(3,65,180,0.4) 66%)";

/* Айсберг: контейнер с обрезкой + картинка с точными смещениями из макета.
   anim задаёт всплытие (entrance) и покачивание на воде (idle) через CSS-переменные.
   Без anim — статичная льдина без анимации. */
function Ice({ src, box, img, anim }) {
  const { delay = 0, dur = 7, dist = 40, bob = 10, rot = 1.5 } = anim || {};
  const style = anim
    ? {
        ...box,
        "--ice-delay": `${delay}s`,
        "--ice-idle-delay": `${delay + 1}s`,
        "--ice-dur": `${dur}s`,
        "--ice-dist": `${dist}px`,
        "--ice-bob": `${bob}px`,
        "--ice-rot": `${rot}deg`,
      }
    : box;
  return (
    <div
      className={clsx(
        "absolute overflow-hidden pointer-events-none select-none",
        anim && "ice-a"
      )}
      style={style}
    >
      <img src={src} alt="" aria-hidden className="absolute max-w-none" style={img} />
    </div>
  );
}

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="relative overflow-hidden bg-white mt-[55px] lg:mt-[70px]">
      {/* ── Desktop ── */}
      <div className="hidden lg:block relative h-[500px] overflow-hidden">
        <img
          src={oceanImg}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />

        {/* Айсберги — позиции и кропы из макета (1520x500) */}
        <Ice
          src={iceApple}
          box={{ left: "107px", top: "-62px", width: "417px", height: "284px" }}
          img={{ width: "416.79%", height: "318.66%", left: "-95.68%", top: "0" }}
          anim={{ delay: 0, dur: 6.5, dist: 36, bob: 9, rot: 1.2 }}
        />
        <Ice
          src={iceBitcoin}
          box={{ left: "-141px", top: "-46px", width: "545px", height: "426px" }}
          img={{ width: "240.39%", height: "159.89%", left: "0", top: "0" }}
          anim={{ delay: 0.15, dur: 7.5, dist: 44, bob: 12, rot: -1.6 }}
        />
        <Ice
          src={iceTesla}
          box={{ left: "-67px", top: "203px", width: "376px", height: "330px" }}
          img={{ width: "217.79%", height: "129.29%", left: "0", top: "-29.29%" }}
          anim={{ delay: 0.3, dur: 8, dist: 32, bob: 8, rot: 1.4 }}
        />
        <Ice
          src={iceEuro}
          box={{ right: "-121px", top: "-42px", width: "639px", height: "368px" }}
          img={{ width: "215.34%", height: "194.7%", left: "-115.34%", top: "-0.07%" }}
          anim={{ delay: 0.45, dur: 7, dist: 40, bob: 11, rot: -1.3 }}
        />
        <Ice
          src={iceAmazon}
          box={{ right: "0", top: "252px", width: "486px", height: "360px" }}
          img={{ width: "285.39%", height: "200.67%", left: "-185.39%", top: "-100.67%" }}
          anim={{ delay: 0.6, dur: 6.8, dist: 38, bob: 10, rot: 1.5 }}
        />

        {/* Контент по центру */}
        <Fade triggerOnce direction="down" duration={800} className="relative h-full">
        <div className="h-full flex flex-col items-center text-center pt-[80px]">
          <h1 className="font-['Inter'] font-black text-[72px] leading-[1.3] text-white">
            {t("trading.banner.title1")}
          </h1>
          <p className="mt-[4px] font-['Inter'] font-black text-[42px] leading-[1.3] text-white">
            {t("trading.banner.title2")}
          </p>
          <p className="mt-[25px] font-['Inter'] font-normal text-[16px] leading-[1.5] text-white max-w-[434px]">
            {t("trading.banner.subtitle")}
          </p>
          <a
            href={link}
            className="mt-[45px] inline-flex items-center justify-center px-[42px] py-[18px] rounded-[40px] border border-white font-['Inter'] font-bold text-[21px] text-white capitalize backdrop-blur-[15px] hover:opacity-90 transition-opacity"
            style={{ backgroundImage: BTN_GRADIENT_LIGHT }}
          >
            {t("trading.banner.cta")}
          </a>
        </div>
        </Fade>
      </div>

      {/* ── Mobile / tablet ── */}
      <div className="lg:hidden relative h-[632px] overflow-hidden">
        <img
          src={oceanImg}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />

        {/* Айсберги — из мобильного макета (372x632) */}
        <div
          className="absolute left-0 top-[264px] w-[60.8%] aspect-[226/224] overflow-hidden pointer-events-none select-none ice-a"
          style={{
            "--ice-delay": "0s",
            "--ice-idle-delay": "1s",
            "--ice-dur": "7s",
            "--ice-dist": "36px",
            "--ice-bob": "9px",
            "--ice-rot": "1.2deg",
          }}
        >
          <div className="absolute w-[174.3%] aspect-[319/249]">
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={iceBitcoinmob}
                alt=""
                aria-hidden
                className="absolute max-w-none"
                style={{left: "0", top: "0" }}
              />
            </div>
          </div>
        </div>
        <Ice
          src={iceEuro}
          box={{ left: "60.8%", top: "307px", width: "39.2%", height: "195px" }}
          img={{ width: "694.68%", height: "270.59%", left: "-372.08%", top: "-14.45%" }}
          anim={{ delay: 0.15, dur: 7.5, dist: 32, bob: 10, rot: -1.4 }}
        />
        <Ice
          src={iceTesla}
          box={{ left: "-33px", bottom: "-1px", width: "214px", height: "183px" }}
          img={{ width: "217.79%", height: "132.82%", left: "0", top: "-30.09%" }}
        />
        <Ice
          src={iceAmazon}
          box={{ right: "0px", bottom: "-1px", width: "205px", height: "169px" }}
          img={{ width: "317.4%", height: "200.67%", left: "-206.19%", top: "-100.67%" }}
        />

        {/* Контент */}
        <Fade triggerOnce direction="down" duration={800} className="relative">
        <div className="flex flex-col items-center text-center pt-[25px] px-[18px]">
          <h1 className="font-['Inter'] font-black text-[40px] leading-[1.3] text-white">
            {t("trading.banner.title1")}
          </h1>
          <p className="font-['Inter'] font-black text-[23px] leading-[1.3] text-white">
            {t("trading.banner.title2")}
          </p>
          <p className="mt-[12px] font-['Inter'] font-normal text-[14px] leading-[1.5] text-white max-w-[320px]">
            {t("trading.banner.subtitle")}
          </p>
          <a
            href={link}
            className="mt-[35px] inline-flex items-center justify-center px-[28px] py-[12px] rounded-[26px] border-[0.66px] border-white font-['Inter'] font-bold text-[14px] text-white capitalize backdrop-blur-[10px]"
            style={{ backgroundImage: BTN_GRADIENT_LIGHT }}
          >
            {t("trading.banner.cta")}
          </a>
        </div>
        </Fade>
      </div>
    </section>
  );
}

export default Banner;
